import { useEffect, useRef } from 'react';

/**
 * Dismisses after `durationMs`, but pausing banks the time left instead of restarting the clock.
 * Restarting would let a user keep the overlay alive indefinitely just by sweeping the pointer
 * across it, which is the behaviour this whole feature exists to fix.
 *
 * `durationMs: null` never dismisses.
 */
export const useAutoDismissTimer = (onDismiss: () => void, durationMs: number | null, paused: boolean) => {
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  const remainingRef = useRef(durationMs);

  // Must be its own effect, declared first: React runs every cleanup before any effect body, so the
  // timer's cleanup banks time on a duration change too. This runs after that and overwrites it.
  useEffect(() => {
    remainingRef.current = durationMs;
  }, [durationMs]);

  useEffect(() => {
    const remaining = remainingRef.current;

    if (paused || remaining === null) {
      return;
    }

    const startedAt = performance.now();
    const timeoutId = window.setTimeout(() => onDismissRef.current(), remaining);

    return () => {
      window.clearTimeout(timeoutId);
      remainingRef.current = Math.max(0, remaining - (performance.now() - startedAt));
    };
  }, [durationMs, paused]);
};
